/**
 * Central frontend error capture.
 *
 * Every unexpected error funnels through captureError(), which reports to
 * PostHog (always available — the client is initialized at app boot) and to
 * Sentry when VITE_SENTRY_DSN is configured. Sentry is loaded dynamically so
 * the bundle carries no Sentry weight while the DSN is absent.
 *
 * Wiring (see initErrorCapture / app.ts):
 * - Vue app.config.errorHandler: render/lifecycle/watcher errors
 * - Inertia router 'exception' events
 * - TanStack Query/Mutation cache onError (app.ts QueryClient)
 * - ErrorBoundary components report through captureError before swallowing
 * - Unhandled window errors/rejections are captured by PostHog's exception
 *   autocapture (capture_exceptions in lib/posthog.ts), not duplicated here.
 */
import posthog from 'posthog-js'
import type { App } from 'vue'

export type CaptureContext = Record<string, unknown>

interface CapturedUser {
    id: string | number
    email?: string
    isAdminImpersonating?: boolean
}

/**
 * Errors that carry no signal: browser quirks, aborted requests, session
 * expiry (bootstrap.js already redirects to /login), and stale-deploy chunk
 * loads (a reload resolves them).
 */
const NOISE_PATTERNS: RegExp[] = [
    /ResizeObserver loop/i,
    /^Script error\.?$/,
    /AbortError|Request aborted|canceled/i,
    /Unauthenticated|status code 401|status code 419/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
]

/**
 * HTTP statuses that are expected, user-driven, or already handled elsewhere
 * and would only flood the tracker: 401/419 session expiry (bootstrap.js
 * redirects), 422 form validation (surfaced inline to the user).
 */
const NOISE_STATUSES = new Set([401, 419, 422])

const MAX_CAPTURES_PER_MESSAGE = 3
const captureCounts = new Map<string, number>()

type SentryModule = typeof import('@sentry/vue')
let sentry: SentryModule | null = null
let lastSentryUser: string | null = null

/**
 * Reads the HTTP status off either an HttpError (`.status`, the shape
 * everything through lib/http.ts throws) or a raw AxiosError
 * (`.response.status`), so a 422 that skipped the http.ts wrapper is still
 * treated as noise.
 */
function statusOf(error: unknown): number | undefined {
    const direct = (error as { status?: unknown })?.status
    if (typeof direct === 'number') {
        return direct
    }
    const nested = (error as { response?: { status?: unknown } })?.response
        ?.status
    return typeof nested === 'number' ? nested : undefined
}

export function isNoise(error: unknown): boolean {
    const status = statusOf(error)
    if (status !== undefined && NOISE_STATUSES.has(status)) {
        return true
    }
    const message = error instanceof Error ? error.message : String(error)
    return NOISE_PATTERNS.some(pattern => pattern.test(message))
}

function toError(value: unknown): Error {
    if (value instanceof Error) {
        return value
    }
    return new Error(typeof value === 'string' ? value : JSON.stringify(value))
}

function underCaptureLimit(message: string): boolean {
    const count = captureCounts.get(message) ?? 0
    if (count >= MAX_CAPTURES_PER_MESSAGE) {
        return false
    }
    captureCounts.set(message, count + 1)
    return true
}

export function captureError(error: unknown, context: CaptureContext = {}) {
    if (isNoise(error)) {
        return
    }
    const err = toError(error)
    if (!underCaptureLimit(err.message)) {
        return
    }

    const properties = { source: 'frontend', ...context }
    try {
        posthog.captureException(err, properties)
    } catch {
        // Reporting must never take the app down with it.
    }
    try {
        sentry?.captureException(err, { extra: properties })
    } catch {
        // Same.
    }
}

/**
 * Installs the Vue global error handler and the Inertia exception hook.
 * Errors keep flowing to the console so local debugging is unchanged.
 */
export function initErrorCapture(app: App) {
    app.config.errorHandler = (error, _instance, info) => {
        console.error(`[vue] error in ${info}:`, error)
        captureError(error, { source: 'vue', vue_info: info })
    }
}

/**
 * Initializes Sentry when a DSN is configured. No-op (and no bundle cost —
 * dynamic import) without one. Session replay is deliberately left off:
 * PostHog session recording already covers it.
 */
export async function initSentry(app: App): Promise<void> {
    const dsn = import.meta.env.VITE_SENTRY_DSN
    if (!dsn) {
        return
    }
    try {
        const Sentry = await import('@sentry/vue')
        Sentry.init({
            app,
            dsn,
            environment:
                import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
            sendDefaultPii: true,
            tracesSampleRate: parseFloat(
                import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'
            ),
            beforeSend: event => {
                event.tags = { ...(event.tags || {}), source: 'frontend' }
                const message =
                    event.exception?.values?.[0]?.value ?? event.message ?? ''
                if (NOISE_PATTERNS.some(pattern => pattern.test(message))) {
                    return null
                }
                return event
            },
        })
        sentry = Sentry
        // init is async; a navigation may have called setErrorUser and cached
        // its user before Sentry finished loading — flush it now so the first
        // page's errors still carry user context.
        if (pendingUser) {
            applyUserToSentry(pendingUser)
        }
    } catch (error) {
        console.error('Sentry initialization failed:', error)
    }
}

let pendingUser: CapturedUser | null = null

function applyUserToSentry(user: CapturedUser) {
    if (!sentry) {
        return
    }
    const serialized = JSON.stringify(user)
    if (serialized === lastSentryUser) {
        return
    }
    lastSentryUser = serialized
    sentry.setUser({
        id: String(user.id),
        email: user.email,
        isAdminImpersonating: user.isAdminImpersonating ?? false,
    })
}

/**
 * Mirrors the identified user into Sentry (PostHog identify happens in
 * lib/posthog.ts). Impersonation is tagged, not skipped — errors during
 * impersonated sessions are still product errors. The user is cached so a
 * call that lands before the async Sentry init resolves is not lost (see
 * initSentry).
 */
export function setErrorUser(user: CapturedUser) {
    pendingUser = user
    applyUserToSentry(user)
}

/** Test-only escape hatches. */
export function _resetForTesting() {
    captureCounts.clear()
    sentry = null
    lastSentryUser = null
    pendingUser = null
}

export function _setSentryForTesting(module: SentryModule | null) {
    sentry = module
}
